import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { hasValidRefreshToken, hasValidAccessToken, useUserStore } from './stores/userStore';
import { B2cLayout } from './pages/(b2c)/layout/B2cLayout';
import { AppUpdateManager } from './pages/(b2c)/layout/AppUpdateManager';
import { ThemeProvider } from './context/ThemeContext';
import { RealtimeProvider } from './context/RealtimeContext';
import { initializeApiClient } from './setup';
import { refreshTokenAsync } from './utils/apiInterceptor';

const B2cAuthPage = React.lazy(() => import('./pages/(b2c)/auth/page'));
const B2cOnboardingPage = React.lazy(() => import('./pages/(b2c)/onboarding/page'));
const B2cHomePage = React.lazy(() => import('./pages/(b2c)/home/page'));
const B2cAlertsPage = React.lazy(() => import('./pages/(b2c)/alerts/page'));
const B2cDailySummaryPage = React.lazy(() => import('./pages/(b2c)/daily-summary/page'));
const B2cVideosPage = React.lazy(() => import('./pages/(b2c)/videos/page'));
const B2cMyPage = React.lazy(() => import('./pages/(b2c)/mypage/page'));
const B2cPetDetailPage = React.lazy(() => import('./pages/(b2c)/pet/page'));
const B2cPetEditPage = React.lazy(() => import('./pages/(b2c)/pet/edit/page'));
const B2cPetRegisterPage = React.lazy(() => import('./pages/(b2c)/pet/register/page'));
const B2cInstallPage = React.lazy(() => import('./pages/(b2c)/install/page'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

initializeApiClient();

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#72734e] border-t-transparent" />
        <p className="text-sm text-neutral-500">로딩 중...</p>
      </div>
    </div>
  );
}

function RootRedirect() {
  const { pathname } = useLocation();
  if (pathname === '/login') return <Navigate to="/b2c/auth" replace />;
  return <Navigate to="/b2c/home" replace />;
}

function B2cProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useUserStore(state => state.isAuthenticated);
  const setAuthenticated = useUserStore(state => state.setAuthenticated);
  const [sessionStatus, setSessionStatus] = React.useState<'checking' | 'ready' | 'unauthenticated'>(() => {
    if (hasValidAccessToken()) return 'ready';
    return hasValidRefreshToken() ? 'checking' : 'unauthenticated';
  });
  const refreshInFlightRef = React.useRef<Promise<void> | null>(null);

  const verifySession = React.useCallback(async () => {
    if (hasValidAccessToken()) {
      if (!isAuthenticated) {
        setAuthenticated(true);
      }
      setSessionStatus('ready');
      return;
    }

    if (!hasValidRefreshToken()) {
      setSessionStatus('unauthenticated');
      return;
    }

    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    setSessionStatus('checking');
    const refreshPromise = (async () => {
      if (hasValidAccessToken()) {
        if (!isAuthenticated) {
          setAuthenticated(true);
        }
        setSessionStatus('ready');
        return;
      }

      if (!hasValidRefreshToken()) {
        setSessionStatus('unauthenticated');
        return;
      }

      const refreshResult = await refreshTokenAsync();

      if (refreshResult.token) {
        setAuthenticated(true);
        setSessionStatus('ready');
      } else {
        setSessionStatus('unauthenticated');
      }
    })().finally(() => {
      refreshInFlightRef.current = null;
    });

    refreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [isAuthenticated, setAuthenticated]);

  React.useEffect(() => {
    void verifySession();
  }, [verifySession]);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if ((isAuthenticated || hasValidRefreshToken()) && !hasValidAccessToken()) {
        void verifySession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [isAuthenticated, verifySession]);

  if (sessionStatus === 'unauthenticated') {
    return <Navigate to="/b2c/auth" replace />;
  }

  if (sessionStatus === 'checking') {
    return <PageLoader />;
  }

  if (!hasValidAccessToken()) {
    if (hasValidRefreshToken()) {
      return <PageLoader />;
    }
    return <Navigate to="/b2c/auth" replace />;
  }

  return <>{children}</>;
}

export default function B2cApp() {
  // localStorage가 iOS ITP(7일 미사용)·저장압박으로 축출되어 토큰이 사라지는 것을 완화
  React.useEffect(() => {
    void navigator.storage?.persist?.();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <RealtimeProvider>
            <React.Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Navigate to="/b2c/home" replace />} />
                <Route path="/login" element={<Navigate to="/b2c/auth" replace />} />
                <Route path="/b2c" element={<Navigate to="/b2c/home" replace />} />
                <Route path="/b2c/install" element={<B2cInstallPage />} />
                <Route path="/b2c/auth" element={<B2cLayout hideNav><B2cAuthPage /></B2cLayout>} />
                <Route path="/b2c/login" element={<Navigate to="/b2c/auth" replace />} />
                <Route path="/b2c/onboarding" element={<B2cLayout hideNav><B2cOnboardingPage /></B2cLayout>} />
                <Route path="/b2c/home" element={<B2cProtectedRoute><B2cLayout><B2cHomePage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="/b2c/alerts" element={<B2cProtectedRoute><B2cLayout><B2cAlertsPage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="/b2c/daily-summary" element={<B2cProtectedRoute><B2cLayout><B2cDailySummaryPage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="/b2c/videos" element={<B2cProtectedRoute><B2cLayout><B2cVideosPage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="/b2c/mypage" element={<B2cProtectedRoute><B2cLayout><B2cMyPage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="/b2c/pet" element={<B2cProtectedRoute><B2cLayout hideNav><B2cPetDetailPage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="/b2c/pet/edit" element={<B2cProtectedRoute><B2cLayout hideNav><B2cPetEditPage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="/b2c/pet/register" element={<B2cProtectedRoute><B2cLayout hideNav><B2cPetRegisterPage /></B2cLayout></B2cProtectedRoute>} />
                <Route path="*" element={<RootRedirect />} />
              </Routes>
              <AppUpdateManager />
            </React.Suspense>
          </RealtimeProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
